import { useMemo } from 'react';

import { CreditType } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { type useCustomerCredit } from '../useCustomerCredit';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns filtered stipend credits and total amount based on the associated
 * feature flag.
 */
export const useStipendCredits = (
  params: UseStipendCreditsParams,
): UseStipendCreditsReturnType => {
  const { credits, availableCredit } = params;

  const shouldSeparatePromoAndGiftCard = useFeatureFlag(
    'CELS-1410-separate-promo-and-gift-card',
  );

  // ─────────────────────────────────────────────────────────────────────

  const { filteredCredits, totalCreditAmount } =
    useMemo<UseStipendCreditsReturnType>(() => {
      if (!shouldSeparatePromoAndGiftCard)
        return {
          filteredCredits: credits,
          totalCreditAmount: availableCredit,
        };

      const creditsList = credits.filter(
        (credit) =>
          credit?.creditType === CreditType.InStoreOnly ||
          credit?.creditType === CreditType.OnlineOnly,
      );
      const totalAmount = creditsList.reduce(
        (total, currentCredit) => total + currentCredit.remainingAmount,
        0,
      );

      return { filteredCredits: creditsList, totalCreditAmount: totalAmount };
    }, [availableCredit, credits, shouldSeparatePromoAndGiftCard]);

  // ─────────────────────────────────────────────────────────────────────

  return { filteredCredits, totalCreditAmount };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseStipendCreditsParams = Readonly<{
  credits: ReturnType<typeof useCustomerCredit>['credits'];
  availableCredit: number;
}>;

type UseStipendCreditsReturnType = Readonly<{
  filteredCredits: ReturnType<typeof useCustomerCredit>['credits'];
  totalCreditAmount: number;
}>;
